import { AsyncPipe } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DrawerComponent } from '@progress/kendo-angular-layout';
import { Observable, tap } from 'rxjs';
import { PermissionStorageService } from './authentication/storage/permission-storage.service';
import { TokenStorageService } from './authentication/storage/token-storage.service';
import { IEmployee, Language } from './company/models/employee';
import { EmployeeStoreService } from './company/stores/employee-store.service';
import { ConnectivityStateService } from './shared/services/connectivity-state.service';
import { EmployeeService } from './company/services/employee.service';
import { AppStateInterface } from './stores/appState.interface';
import { Store, select } from '@ngrx/store';
import { employeeSelector } from './stores/global/employees/employee-selectors';
import * as EmployeeActions from "./stores/global/employees/employee-actions";
import { currentEmployeeSelector } from './stores/global/current-employee/current-employee-selector';
import { environment } from '../environments/environment';
import { companyHasFeature } from './shared/helpers/tenant';


@UntilDestroy()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
  title = 'HänyTec App';
  public expanded = false;
  readonly version = environment.version;
  isActive = false;
  public items: Array<{ [Key: string]: unknown }> = [];
  currentUrl: string = window.location.pathname.substring(1);
  currentEmployee$: Observable<any>;
  currentEmployee: any = null;
  connectionState = true;

  constructor(
    public router: Router,
    private titleService: Title,
    private tokenStorageSerivce: TokenStorageService,
    private permissionStorage: PermissionStorageService,
    private route: ActivatedRoute,
    private translocoService: TranslocoService,
    private connectivityStateService: ConnectivityStateService,
    private employeeStore: EmployeeStoreService,
    private store: Store<AppStateInterface>
  ) {
    this.currentEmployee$ = this.store.pipe(select(currentEmployeeSelector));
  }

  public connectionState$: Observable<boolean> = this.connectivityStateService.connectionState$;

  ngOnInit() {
    this.connectionState$.pipe(untilDestroyed(this)).subscribe((val) => {
      this.connectionState = val;
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          if (this.router.url != '/auth') {
            this.isActive = true;
            this.generateMenuItems(val);
          } else {
            this.isActive = false;
          }
        }
      });

      this.generateMenuItems(val);
    });

    this.initCurrentEmployee();
    this.setLanguage();

    // menu entries can depend on per-company feature flags (e.g. inventory module), so regenerate once the employee is loaded
    this.currentEmployee$
      .pipe(untilDestroyed(this))
      .subscribe(employee => {
        this.currentEmployee = employee;
        this.generateMenuItems(this.connectionState);
      });
  }

  initCurrentEmployee() {
    if (this.tokenStorageSerivce.getToken()) {
      this.store.dispatch(EmployeeActions.getCurrentEmployee());
    }
  }

  get languages(){
    return Language
  }

  setLanguage(){
    this.translocoService.setActiveLang(this.employeeStore.getLanguage() ?? "de")
    this.currentEmployee$
      .pipe(untilDestroyed(this))
      .subscribe(employee => {
        if(employee){
          this.translocoService.setActiveLang(employee.language == this.languages.french ? "fr" : "de")
        }
      })
  }

  onLogout() {
    this.tokenStorageSerivce.signOut();
    this.employeeStore.resetCurrentEmployee();
    // this.router.navigate(['/auth'])
    window.open('/auth',"_self") // this loads the logout page, and kills all global stores
  }

  generateMenuItems(connectionState: boolean = true) {
    this.items = [];
    const routes: any[] = this.router.config;

    routes.forEach((route) => {
      if (
        route.data?.title &&
        this.permissionStorage.getPermissions()?.includes(route.data.permission) &&
        !this.permissionStorage.getPermissions()?.includes(route.data?.hideWithPermission) &&
        companyHasFeature(this.currentEmployee?.company, route.data?.requiresCompanyFeature) &&
        route.data?.title != 'profile.title'
      ) {
        if ((connectionState && route.data?.hideOnline != true) || (!connectionState && route.data?.hideOffline != true)) {

          if(!connectionState) {
            this.items.push({
              text: this.translocoService.translate(route.data.title),
              path: route.path ? route.path : "",
              children: route.children ? true : false,
              selected: route.path === this.router.url.slice(1) ? true : false,
              iconName: route.data?.iconName,
              disabled: route.data?.disableOffline
            });
          }
          else if (connectionState) {
            this.items.push({
              text: this.translocoService.translate(route.data.title),
              path: route.path ? route.path : "",
              children: route.children ? true : false,
              selected: route.path === this.router.url.slice(1) ? true : false,
              iconName: route.data?.iconName
            });
          }

          if (route.path === this.router.url.slice(1)) {
            this.setTitleTag(
              this.translocoService.translate(route.data.title)
            )
          }

          if (route.children) {
            route.children.forEach((child: { data: { title: any; level: any; }; path: any; }) => {
              this.items.push({
                text: this.translocoService.translate(child.data.title),
                path: child.path ? route.path + '/' + child.path : "",
                level: child.data.level,
                selected: route.path + '/' + child.path === this.router.url.slice(1) ? true : false,
              })
            })
          }
        }
      }
    });
  }

  setTitleTag(pageName: string) {
    this.titleService.setTitle(pageName + " | HänyTec App");
  }
}
